import type { InputBaseProps } from '@mui/material'
import { InputBase, Typography, useTheme } from '@mui/material'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, Persisted } from '@tunasong/schemas'
import type { FC } from 'react'
import React, { useEffect } from 'react'
import { focusNextElement } from '../dom/focus.js'
import { useDelayedInvoke } from '../hooks/delayed-invoke.js'
import { VBox } from '../layout/vertical-box.js'
import { disableAutocomplete } from '../util/disable-autocomplete.js'

export interface EntityTitleProps extends InputBaseProps {
  entity?: Persisted<Entity>
}

export const EntityTitle: FC<EntityTitleProps> = props => {
  const { entity, ...inputProps } = props
  const updateEntity = graphHooks.useEntityUpdate({ debounceDelay: 1000 })
  const theme = useTheme()
  const inputRef = React.useRef<HTMLInputElement>(null)

  /** @note that on onmount the active invoke timeout will be cancelled  */
  const delayedTitle = useDelayedInvoke(200)

  const hasEdited = React.useRef(false)

  const handleTitle: React.FocusEventHandler<HTMLInputElement> = e => {
    if (!entity) {
      return
    }
    hasEdited.current = true
    delayedTitle(() => {
      updateEntity(entity.id, { name: e.target.value })
    })
  }
  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      if (inputRef.current) {
        focusNextElement(inputRef.current)
      }
    }
  }

  // Update the input value if the entity name changes and the user has not edited it
  useEffect(() => {
    if (hasEdited.current || !inputRef.current) {
      // Never update the title if the user has already edited it
      return
    }
    inputRef.current.value = entity?.name ?? ''
  }, [entity?.name])

  if (!entity) {
    return null
  }

  return (
    <VBox sx={{ alignItems: 'center' }}>
      <InputBase
        key={entity.id}
        sx={{ ...theme.typography.h1, textAlign: 'center', ...disableAutocomplete, mb: 0, mt: 0 }}
        placeholder={'Untitled'}
        inputRef={inputRef}
        inputProps={{ style: { textAlign: 'center' } }}
        defaultValue={entity.name ?? ''}
        fullWidth
        autoFocus={!entity.name}
        autoCorrect="off"
        onKeyDown={handleKeyDown}
        onChange={handleTitle}
        {...inputProps}
      />
      {entity.description ? <Typography variant="caption">{entity.description}</Typography> : null}
    </VBox>
  )
}

export default EntityTitle
